import { useEffect, useState } from "react";
import useApiStatus from "../../../../../hooks/useApiStatus";
import { removelocalStorageToken } from "../../../../../utilities/sessionStorage/token";
import AdminService from "../../../../../service/AdminService";
import { removelocalStorageUsername } from "../../../../../utilities/sessionStorage/username";
import OrderService from "../../../../../service/OrderService";
import ShippingService from "../../../../../service/ShippingService";
import { formatDate } from "../../../../../utilities/formatDate";

const useOrders = ({
  sales,
  admin,
  onErrorLoadAdminOrders,
  onErrorLoadUserOrders,
  onErrorReceiveOrder,
  onSuccessReceiveOrder,
  onErrorSendOrder,
  onErrorFillOutAll,
  onSuccessSendOrder,
  onErrorDownloadProducts,
  onErrorGetParcel,
  onErrorProvideRating,
  onErrorOrderRate,
  onSuccessOrderRate,
  onErrorValidatePickup,
  onErrorRegisterPickup,
}) => {
  const { isLoading, hasError, isSuccess, retryFetch, setIsLoading, setIsSuccess, setHasError } =
    useApiStatus({
      isLoading: true,
    });

  const [loadingLabel, setLoadingLabel] = useState(false);
  const [loadingPickup, setLoadingPickup] = useState(false);
  const [confirmReceivedVisible, setConfirmReceivedVisible] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [orders, setOrders] = useState([]);
  const [links, setLinks] = useState([]);
  const [parcelInfo, setParcelInfo] = useState(null);
  const [orderId_, setOrderId_] = useState(null);
  const [confirmSendVisible, setConfirmSendVisible] = useState(false);
  const [ratingProduct, setRatingProduct] = useState(null);
  const [ratingSeller, setRatingSeller] = useState(null);
  const [displayRateVisible, setDisplayRateVisible] = useState(false);
  const [comment, setComment] = useState("");
  const [commentProductService, setCommentProductService] = useState("");

  const [currency, setCurrency] = useState("$");
  const [shippingCompany, setShippingCompany] = useState("");
  const [trackingObject, setTrackingObject] = useState("");

  const [dateBegin, setDateBegin] = useState(null);
  const [dateEnd, setDateEnd] = useState(null);
  const [displayRegisterPickupVisible, setDisplayRegisterPickupVisible] = useState(false);

  const [storeName, setStoreName] = useState("");

  const adminService = new AdminService();
  const orderService = new OrderService();
  const shippingService = new ShippingService();

  const loadAdminOrders = async (cancel) => {
    try {
      setIsLoading(true);

      const res = await adminService.getAllOrders(localStorage.getItem("__extraconomy_token"));
      if (cancel) return;

      if (res.messageCode !== 200001) {
        onErrorLoadAdminOrders(res.messageCode);
        setIsLoading(false);
      } else {
        const orderFormatedData = res?.result?.map((order) => {
          order.formatedDate = formatDate(order.date);

          return order;
        });
        setOrders(orderFormatedData);
        setIsLoading(false);
        return orderFormatedData;
      }
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        removelocalStorageToken();
        removelocalStorageUsername();
        window.location.replace("/login");
      }
      setIsLoading(false);
      console.log(e);
    }
  };

  const loadUserOrders = async (cancel) => {
    try {
      setIsLoading(true);

      var res;
      if (!sales) {
        res = await orderService.getPurchases(
          localStorage.getItem("__extraconomy_token"),
          localStorage.getItem("__extraconomy_username")
        );
        if (cancel) return;
      } else {
        res = await orderService.getSales(
          localStorage.getItem("__extraconomy_token"),
          localStorage.getItem("__extraconomy_username")
        );
        if (cancel) return;
      }
      if (cancel) return;

      if (res.messageCode !== 200001) {
        console.log(res);
        onErrorLoadUserOrders(res.messageCode);
        setIsLoading(false);
      } else {
        setOrders(res.result.filter((order) => order.status !== "PENDING"));
        setIsLoading(false);
        return res.result.filter((order) => order.status !== "PENDING");
      }
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        removelocalStorageToken();
        removelocalStorageUsername();
        window.location.replace("/login");
      }
      setIsLoading(false);
      console.log(e);
    }
  };
  const filterByStoreName = async () => {
    await loadAdminOrders().then((res) => {
      if (storeName) {
        const filteredData = res.filter(
          (obj) =>
            obj.seller.companyName?.toLowerCase() === storeName?.toLowerCase() ||
            obj.seller.firstname?.toLowerCase() === storeName?.toLowerCase()
        );
        setOrders(filteredData);
      }
    });
  };

  // `order` permite confirmar o recebimento de um pedido que não é o currentOrder
  // (ex.: lembrete de pedidos enviados). Retorna true em caso de sucesso.
  async function receiveOrder(username, orderId, order = currentOrder) {
    try {
      setIsLoading(true);

      let res;

      if (!order.itens[0].product.downloadable)
        res = await orderService.postReceiveOrder(
          localStorage.getItem("__extraconomy_token"),
          username,
          orderId
        );
      else
        res = await orderService.getDownloadLinks(
          localStorage.getItem("__extraconomy_token"),
          username,
          orderId
        );

      if (res.messageCode !== 200000 && res.messageCode !== 200001) {
        console.log(res);
        onErrorReceiveOrder(res.messageCode);
        setIsLoading(false);
        return false;
      } else {
        setIsLoading(false);
        setConfirmReceivedVisible(false);
        onSuccessReceiveOrder({ detail: "productReceivedSuccessfully" });

        const orders = await loadUserOrders(false);
        setOrders(orders);
        if (currentOrder && currentOrder.id === order.id)
          setCurrentOrder(orders.find((o) => o.id === order.id));
        return true;
      }
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        removelocalStorageToken();
        removelocalStorageUsername();
        window.location.replace("/login");
      }
      setIsLoading(false);
      console.log(e);
      return false;
    }
  }

  async function sendOrder(username, orderId, shippingCompany, trackingObject) {
    try {
      setIsLoading(true);

      if (
        !currentOrder?.itens[0]?.product?.subCategory?.service &&
        (shippingCompany.length === 0 || trackingObject.length === 0)
      ) {
        onErrorFillOutAll();
        setIsLoading(false);
      } else {
        const res = await orderService.postSendOrder(
          localStorage.getItem("__extraconomy_token"),
          username,
          orderId,
          currentOrder?.itens[0]?.product?.subCategory?.service ? "" : shippingCompany,
          currentOrder?.itens[0]?.product?.subCategory?.service ? "" : trackingObject
        );

        if (res.messageCode !== 200000) {
          console.log(res);
          onErrorSendOrder(res.messageCode);

          setIsLoading(false);
        } else {
          setIsLoading(false);
          setConfirmSendVisible(false);
          onSuccessSendOrder();

          const orders = await loadUserOrders(false);
          setOrders(orders);
          setCurrentOrder(orders.find((order) => order.id === currentOrder.id));
        }
      }
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        removelocalStorageToken();
        removelocalStorageUsername();
        window.location.replace("/login");
      }
      setIsLoading(false);
      console.log(e);
    }
  }

  async function downloadProducts(cancel) {
    try {
      setIsLoading(true);

      const res = await orderService.getDownloadLinks(
        localStorage.getItem("__extraconomy_token"),
        localStorage.getItem("__extraconomy_username"),
        currentOrder.id
      );
      if (cancel) return;

      if (res.messageCode !== 200001) {
        console.log(res);
        onErrorDownloadProducts(res.messageCode);

        if (res.messageCode === 500000) console.log(res);
        setIsLoading(false);
        return false;
      } else return res.result;
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        removelocalStorageToken();
        removelocalStorageUsername();
        window.location.replace("/login");
      }
      setIsLoading(false);
      return false;
    }
  }

  useEffect(() => {
    let cancel = false;

    async function fetchProducts() {
      if (
        currentOrder &&
        currentOrder.status === "RECEIVED" &&
        currentOrder.itens[0].product.downloadable
      ) {
        setIsLoading(true);
        const products = await downloadProducts(cancel);
        if (cancel) return;

        if (products) {
          setLinks(products);
          setIsLoading(false);
        }
      }
    }

    fetchProducts();

    return () => {
      cancel = true;
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrder]);

  async function getParcel(cancel) {
    try {
      if (
        currentOrder &&
        !currentOrder.itens[0].product.service &&
        !currentOrder.itens[0].product.downloadable
      ) {
        setIsLoading(true);
        const res = await shippingService.getParcelInfo(
          currentOrder.id,
          localStorage.getItem("__extraconomy_token")
        );
        if (cancel) return;

        if (res.messageCode !== 200001 && res.messageCode !== 500157) {
          console.log(res);
          setParcelInfo(null);
          onErrorGetParcel(res.messageCode);
          setIsLoading(false);
          return false;
        }

        setParcelInfo(res.result);
        setIsLoading(false);
      }
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        removelocalStorageToken();
        removelocalStorageUsername();
        window.location.replace("/login");
      }
      setIsLoading(false);
      return false;
    }
  }

  // Verify pickup status
  useEffect(() => {
    let cancel = false;

    getParcel(cancel);

    return () => {
      cancel = true;
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrder]);

  useEffect(() => {
    if (parcelInfo?.shippingMethods?.name) setShippingCompany(parcelInfo?.shippingMethods?.name);
    if (parcelInfo?.trackingNumber) setTrackingObject(parcelInfo?.trackingNumber);
  }, [parcelInfo]);

  async function orderRate() {
    try {
      setIsLoading(true);

      if (!ratingProduct || !ratingSeller) {
        onErrorProvideRating();
        setIsLoading(false);
        return false;
      }

      const res = await orderService.postRateOrder(
        localStorage.getItem("__extraconomy_token"),
        localStorage.getItem("__extraconomy_username"),
        comment,
        commentProductService,
        orderId_,
        ratingProduct,
        ratingSeller
      );

      if (res.messageCode !== 200000) {
        onErrorOrderRate(res.messageCode);

        if (res.messageCode === 500000) console.log(res);
        setIsLoading(false);
        setDisplayRateVisible(false);
      } else {
        setDisplayRateVisible(false);
        onSuccessOrderRate();
        setIsLoading(false);

        const orders = await loadUserOrders(false);
        setOrders(orders);
        setIsLoading(false);
      }
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        removelocalStorageToken();
        removelocalStorageUsername();
        window.location.replace("/login");
      }
      console.log(e);
    }
  }

  async function downloadLabel() {
    try {
      setLoadingLabel(true);

      const res = await shippingService.getLabel(
        currentOrder.id,
        localStorage.getItem("__extraconomy_token")
      );

      const url = window.URL.createObjectURL(new Blob([res], { type: "application/pdf" }));
      var link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "label.pdf");
      document.body.appendChild(link);
      link.click();

      setLoadingLabel(false);
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        removelocalStorageToken();
        removelocalStorageUsername();
        window.location.replace("/login");
      }
      setIsLoading(false);
      console.log(e);
    }
  }

  async function validatePickup() {
    try {
      setLoadingPickup(true);

      if (!dateBegin || !dateEnd) {
        onErrorValidatePickup();
        setLoadingPickup(false);
        return false;
      }

      setDisplayRegisterPickupVisible(true);
      setLoadingPickup(false);
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        removelocalStorageToken();
        removelocalStorageUsername();
        window.location.replace("/login");
      }
      setLoadingPickup(false);
      console.log(e);
    }
  }

  async function registerPickup() {
    try {
      setLoadingPickup(true);

      const res = await shippingService.postCreatePickup(
        dateBegin,
        dateEnd,
        currentOrder.id,
        localStorage.getItem("__extraconomy_username"),
        localStorage.getItem("__extraconomy_token")
      );

      if (res.messageCode !== 200000) {
        console.log(res);
        onErrorRegisterPickup(res.messageCode);
        if (res.messageCode === 500000) console.log(res);
        setDisplayRegisterPickupVisible(false);
        setLoadingPickup(false);
        return false;
      }

      if (res.messageCode === 200000) {
        getParcel(false);
      }

      setDisplayRegisterPickupVisible(false);
      setLoadingPickup(false);
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        removelocalStorageToken();
        removelocalStorageUsername();
        window.location.replace("/login");
      }
      setLoadingPickup(false);
      console.log(e);
    }
  }

  useEffect(() => {
    let cancel = false;

    if (!admin) {
      loadUserOrders(cancel);
      if (cancel) return;
    } else {
      loadAdminOrders(cancel);
      if (cancel) return;
    }

    return () => (cancel = true); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isLoading,
    loadingLabel,
    loadingPickup,
    isSuccess,
    hasError,
    retryFetch,
    orders,
    confirmReceivedVisible,
    links,
    currentOrder,
    parcelInfo,
    orderId_,
    confirmSendVisible,
    displayRateVisible,
    ratingSeller,
    ratingProduct,
    comment,
    commentProductService,
    currency,
    shippingCompany,
    trackingObject,
    dateBegin,
    dateEnd,
    displayRegisterPickupVisible,
    storeName,
    filterByStoreName,
    setRatingSeller,
    setRatingProduct,
    setConfirmSendVisible,
    setOrderId_,
    setParcelInfo,
    setLinks,
    setOrders,
    setCurrentOrder,
    setIsLoading,
    setLoadingLabel,
    setIsSuccess,
    setHasError,
    loadUserOrders,
    setLoadingPickup,
    setConfirmReceivedVisible,
    setDisplayRateVisible,
    setComment,
    setCommentProductService,
    setCurrency,
    setShippingCompany,
    setTrackingObject,
    setDateBegin,
    setDateEnd,
    setDisplayRegisterPickupVisible,
    registerPickup,
    validatePickup,
    downloadLabel,
    orderRate,
    sendOrder,
    receiveOrder,
    setStoreName,
  };
};

export default useOrders;
